import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "@tanstack/react-router";
import { useMutation } from "@tanstack/react-query";
import { ImSpinner2 } from "react-icons/im";
import { Select } from "../../../components/fields/base/Select";
import { css } from "../../../../styled-system/css";
import { VStack } from "../../../../styled-system/jsx";
import api from "../../../utils/api";
import { initAmazonAdvertisingOAuth } from "./initAmazonAdvertisingOAuth";
import { initSpApiOAuth } from "./initSpApiOAuth";
import { REGION_MARKETPLACE_LIST_MAP } from "../../../utils/marketplaceConstants";
import { setMarketPlace as setGlobalMarketPlace } from "../../../redux/reducers/marketPlace";
import { updateAmzOnboardingStatus } from "../../../redux/reducers/amzOnboardReducer";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { useSellerInfo } from "./useSellerInfo";
import DialogueCard from "../components/DialogueCard";
import SkipToDashboardLink from "../components/SkipToDashboardLink";
import { Button } from "../../../components/core/basic/Button";
import type { RegionOption } from "../shared/types";
import { SECONDARY_CHANNEL_STORAGE_KEY, MARKETPLACE_STORAGE_KEY } from "../shared/constants";
import { resetDocumentTitle } from "../shared/utils";
import { useAdvertisingProfileOptions } from "./useAdvertisingProfileOptions";

export const AmazonApiConnections = () => {
  const { emptyProfileList } = useAdvertisingProfileOptions();
  const { t } = useTranslation(["onboarding", "common", "auth"]);
  const [region, setRegion] = useState<RegionOption | null>(null);
  const [marketPlace, setMarketPlace] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState("");

  const user = useAppSelector((state) => state.user);
  const { organization_type: orgType, current_profile: currentProfileName } = user;

  const amz_onboard = useAppSelector((state) => state.amz_onboard);
  const {
    amazon_advertising_access: amazonAdvertisingConnected,
    amazon_spapi_access: naSpApiConnected,
    eu_amazon_spapi_access: euSpApiConnected,
    fe_amazon_spapi_access: feSpApiConnected,
  } = amz_onboard;

  const errors = useAppSelector((state) => state.errors);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  if (emptyProfileList && !errorMessage && localStorage[SECONDARY_CHANNEL_STORAGE_KEY] !== "amazon") {
    setErrorMessage(
      t(
        ($) =>
          $.onboarding[
            "The Amazon Advertising API was successfully connected, but no available profiles were retrieved. Please ensure at least one profile has a valid payment method and try again. If the profile is still not available, please contact customer success as it may have already been connected to Trellis."
          ],
      ),
    );
  }

  const { data: sellerInfo, isLoading: isSellerInfoLoading } = useSellerInfo({
    refetchInterval: (query) => {
      const regions = query.state.data?.account_region ?? [];
      if (regions.length > 0) return false;

      // Only poll backend for 1 minute, otherwise something's wrong
      const elapsed = Date.now() - query.state.dataUpdatedAt;
      return elapsed > 60000 ? false : 2000;
    },
    refetchIntervalInBackground: true,
  });

  useEffect(() => {
    // If only one applicable region, automatically set to get proper URLs for SP-API
    if (sellerInfo?.account_region?.length === 1) {
      const regionValue = sellerInfo.account_region[0];
      if (regionValue) {
        const regionOptions: Record<string, RegionOption> = {
          NA: { label: t(($) => $.onboarding.regions["North America"]), value: "NA" },
          EU: { label: t(($) => $.onboarding.regions.Europe), value: "EU" },
          FE: { label: t(($) => $.onboarding.regions["Far East"]), value: "FE" },
        };
        const selectedRegion = regionOptions[regionValue];
        if (selectedRegion) {
          setRegion(selectedRegion);
          setMarketPlace(sellerInfo.amazon_seller_marketplaces?.[0] ?? null);
          setErrorMessage("");
        }
      }
    }
  }, [sellerInfo, t]);

  const triggerAmazonOnboardingTasks = useMutation({
    mutationFn: async () => {
      return await api.post("/api/mwsinfotask/");
    },
  });

  const handleApiTasks = () => {
    resetDocumentTitle();

    triggerAmazonOnboardingTasks.mutate();

    if (!["active", "trial_active", "qore_active"].includes(amz_onboard.org_status)) {
      dispatch(updateAmzOnboardingStatus({ ...amz_onboard, org_status: "pre_trial" }));
    }

    void navigate({ to: "/onboarding/v2/summary" });
  };

  const getRegionOptions = () => {
    const regionOptions: Record<string, RegionOption> = {
      NA: {
        label: t(($) => $.onboarding.regions["North America"]),
        value: "NA",
      },
      EU: {
        label: t(($) => $.onboarding.regions.Europe),
        value: "EU",
      },
      FE: {
        label: t(($) => $.onboarding.regions["Far East"]),
        value: "FE",
      },
    };

    return sellerInfo?.account_region?.map((region: string) => regionOptions[region]).filter(Boolean);
  };

  const awaitingSpApiConnection =
    amazonAdvertisingConnected === "connected" &&
    ![naSpApiConnected, euSpApiConnected, feSpApiConnected].includes("connected");

  const showContinueButton =
    localStorage[SECONDARY_CHANNEL_STORAGE_KEY] === "amazon" && amz_onboard.amazon_advertising_access === "connected" ?
      true
    : amazonAdvertisingConnected === "connected" &&
      [naSpApiConnected, euSpApiConnected, feSpApiConnected].includes("connected");

  return (
    <VStack
      css={{
        alignItems: "flex-start",
        width: "100%",
        maxWidth: { base: "100%", lg: "66.666%" },
        marginX: "auto",
        paddingY: "xlarge",
        textAlign: "start",
      }}
      gap="large"
    >
      <VStack gap="xsmall" alignItems="flex-start" width="100%">
        <h2 className={css({ fontWeight: "600" })}>{t(($) => $.onboarding["Connect Amazon"])}</h2>
        <p className={css({ fontSize: "xlarge" })}>
          {t(
            ($) =>
              $.onboarding[
                "To effectively integrate your data we need access to your Amazon Advertising and Amazon Seller APIs."
              ],
          )}
        </p>
        {amazonAdvertisingConnected === "connected" && currentProfileName ?
          <p className={css({ fontSize: "xlarge" })}>
            {t(($) => $.onboarding["Please authorize the SP-API for the profile {currentProfileName}."], {
              currentProfileName,
            })}
          </p>
        : null}
      </VStack>

      {errorMessage ?
        <p
          className={css({
            width: "100%",
            paddingLeft: "small",
            color: "red.500",
            fontSize: "large",
            textAlign: "center",
          })}
        >
          {errorMessage}
        </p>
      : errors.length ?
        <VStack gap="small" width="100%">
          {errors.map((error: unknown) => {
            const err = error as { message?: string; code?: string };
            const message =
              err.code === "SESSION_EXPIRED" ?
                t(($) => $.auth["Your session has expired. Please login again."])
              : err.message;
            return (
              <p
                key={message}
                className={css({
                  width: "100%",
                  paddingLeft: "small",
                  color: "red.500",
                  fontSize: "large",
                  textAlign: "center",
                })}
              >
                {message}
              </p>
            );
          })}
        </VStack>
      : null}

      <VStack gap="medium" width="100%">
        <DialogueCard
          title={t(($) => $.onboarding["Amazon Advertising API"])}
          justify="center"
          isDisabled={amazonAdvertisingConnected === "connected" && !emptyProfileList}
          onPress={() => {
            initAmazonAdvertisingOAuth();
          }}
          successIcon={amazonAdvertisingConnected === "connected"}
        >
          {amazonAdvertisingConnected === "connected" && !emptyProfileList ?
            <span>
              {t(($) => $.onboarding["Already connected. You can connect more Amazon Advertising accounts later."])}
            </span>
          : null}
        </DialogueCard>

        {localStorage[SECONDARY_CHANNEL_STORAGE_KEY] !== "amazon" && (
          <>
            {isSellerInfoLoading && (
              <p className={css({ display: "flex", gap: "small", alignItems: "center", fontSize: "large" })}>
                <ImSpinner2 className={css({ animation: "spin" })} />
                {t(($) => $.onboarding["Loading seller information..."])}
              </p>
            )}
            {(sellerInfo?.account_region?.length ?? 0) > 1 && (
              <Select
                label={t(($) => $.onboarding["Select a Region:"])}
                selectedKey={region?.value ?? null}
                items={getRegionOptions()}
                onSelectionChange={(key) => {
                  const selectedRegion = getRegionOptions()?.find((r) => r.value === key) ?? null;
                  setRegion(selectedRegion);
                  setErrorMessage("");
                }}
                css={{
                  width: "100%",
                  fontSize: "xlarge",
                }}
              />
            )}
            <DialogueCard
              title={t(($) => $.onboarding["Amazon Seller API"])}
              justify="center"
              isDisabled={amazonAdvertisingConnected !== "connected" || !region?.value}
              onPress={() => {
                const initialMarketplace =
                  REGION_MARKETPLACE_LIST_MAP[region!.value as keyof typeof REGION_MARKETPLACE_LIST_MAP].find(
                    (mp: string) => sellerInfo?.amazon_seller_marketplaces?.includes(mp),
                  ) ?? "";
                if (marketPlace) {
                  localStorage.setItem(MARKETPLACE_STORAGE_KEY, marketPlace);
                }
                dispatch(setGlobalMarketPlace(initialMarketplace));
                initSpApiOAuth({
                  region: region!.value as "NA" | "EU" | "FE",
                  marketplace: initialMarketplace,
                  isSeller: orgType === "seller",
                  successPath: "/onboarding/v2/connect",
                  errorPath: "/onboarding/v2/connect",
                });
              }}
              successIcon={
                [naSpApiConnected, euSpApiConnected, feSpApiConnected].includes("connected") ? true
                : awaitingSpApiConnection ?
                  false
                : undefined
              }
            >
              {amazonAdvertisingConnected !== "connected" ?
                <span>{t(($) => $.onboarding["Please connect to Amazon Advertising first."])}</span>
              : isSellerInfoLoading ?
                <span>{t(($) => $.onboarding["Please wait..."])}</span>
              : !region?.value ?
                <span>{t(($) => $.onboarding["Please select a region to authorize the SP-API."])}</span>
              : null}
            </DialogueCard>
          </>
        )}

        {showContinueButton ?
          <Button
            className={css({
              width: "100%",
            })}
            isPending={triggerAmazonOnboardingTasks.isPending}
            onPress={() => {
              handleApiTasks();
            }}
          >
            {t(($) => $.common.actions.Continue)}
          </Button>
        : <SkipToDashboardLink onClick={() => handleApiTasks()} />}
      </VStack>
    </VStack>
  );
};
